import { Link } from 'react-router-dom'

const guide = {
  slug: 'block-porn-on-x-twitter',
  category: 'device',
  kicker: 'X · Twitter',
  title: 'How to block porn on X (Twitter): the settings, what they miss, and what holds',
  seoTitle: 'How to Block Porn on X (Twitter) — Settings + What Holds',
  description: `X's sensitive-content settings hide flagged media until you flip them back. The exact settings, why promo accounts slip through, and what makes the block hold.`,
  published: '2026-09-03',
  minutes: 6,
  intro: `X is where a lot of explicit content reaches people who weren't
          looking for it: a reply, a trending topic, a promoted account. The
          platform gives you settings to hide most of it, and you should use
          them. But they are your settings, on your account, and the feed
          learns fast. This guide covers the settings, what they can't catch,
          and the device-level block that doesn't depend on a toggle.`,
  next: ['block-nsfw-on-reddit-permanently', 'dns-porn-blocker', 'porn-blocker-you-cant-uninstall'],
  body: (
    <>
      <h2>Step 1 — The content settings on X</h2>
      <p>
        On the web (the app hides some of these on iPhone), open
        <b> Settings and privacy → Privacy and safety → Content you see</b>:
      </p>
      <ul>
        <li>Turn off <b>Display media that may contain sensitive content</b>.</li>
        <li>Under <b>Search settings</b>, turn on <b>Hide sensitive content</b> and <b>Remove blocked and muted accounts</b>.</li>
        <li>Under <b>Mute and block → Muted words</b>, add the hashtags and terms that show up in your feed. Set them to mute from everyone, in the timeline and in notifications.</li>
      </ul>
      <p>
        Under <b>Content you see → Topics</b>, remove anything adjacent. If
        the algorithmic "For you" feed is the problem, stay on "Following".
      </p>

      <h2>What the settings can't catch</h2>
      <ul>
        <li><b>Accounts that don't flag their media.</b> The sensitive-content setting relies on the poster (or X) marking the media. Promotional accounts for subscription sites often don't, and they work by reply and quote.</li>
        <li><b>The toggle is yours.</b> Same screen, same account, flipped back in ten seconds.</li>
        <li><b>Logged out, and other accounts.</b> A private window or a second account starts from scratch.</li>
        <li><b>Links out.</b> Most explicit content on X is a doorway to a site elsewhere. The setting hides the doorway; it doesn't lock the destination.</li>
      </ul>

      <h2>Step 2 — Lock the destinations</h2>
      <p>
        A DNS category filter refuses pornography and adult domains for
        every app on the device. The link in the reply, the bio link, the
        subscription site: none of them resolve. This is the layer that
        catches what X's settings miss, because it doesn't care whether a
        post was flagged. It also doesn't filter media that X hosts itself,
        because DNS works on domains, not on posts.
        <Link to="/guides/dns-porn-blocker"> How DNS filtering works</Link>.
      </p>

      <h2>Step 3 — If X itself is the problem, block X</h2>
      <p>
        Honest option: if the feed is where relapses start, block the
        domain. From your SELFCNTRL account, add <b>x.com</b> (and
        <b> twitter.com</b>) as blocked domains, or block the whole
        <b> social</b> category. It applies to the app as well as the browser,
        on every locked device, immediately. Loosening waits twenty-four
        hours. If the problem is the hour rather than the app, a night
        curfew cuts the internet entirely between, say, 11pm and 7am.
      </p>

      <h2>Step 4 — Make it stick</h2>
      <p>
        On iPhone and iPad, the filter is carried by a supervised profile
        that can't be removed from the device and forbids adding a VPN, so
        neither the category block nor the domain block can be undone on
        impulse. <Link to="/guides/supervised-iphone-explained">How supervision works</Link>.
        On Mac and Windows, the filter is tied to your account with the same
        one-way delay: tighten now, loosen tomorrow.
      </p>

      <h2>Side by side</h2>
      <table>
        <thead>
          <tr><th></th><th>X content settings</th><th>DNS category filter</th><th>Block x.com (locked)</th></tr>
        </thead>
        <tbody>
          <tr><td>Hides flagged media in feed</td><td>Yes</td><td>No</td><td>X unavailable</td></tr>
          <tr><td>Catches unflagged promo accounts</td><td>Partly (muted words)</td><td>Their links, yes</td><td>Yes</td></tr>
          <tr><td>Blocks the sites posts link to</td><td>No</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Survives logout / second account</td><td>No</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Off in under a minute</td><td>Yes</td><td>Depends on the attachment</td><td>No — 24-hour delay</td></tr>
        </tbody>
      </table>
    </>
  ),
  faq: [
    {
      q: 'How do I block 18+ content on X?',
      a: `In Settings and privacy → Privacy and safety → Content you see,
          turn off "Display media that may contain sensitive content", and in
          Search settings turn on "Hide sensitive content". Use the web
          version if the app hides the option. Then lock the destinations
          with a device-level filter, because the setting is yours to flip.`
    },
    {
      q: 'Why do I still see porn on X with sensitive content off?',
      a: `The setting hides media that was flagged as sensitive. Accounts
          that don't flag their posts, especially promotional ones, get
          through. Muted words help; a DNS filter blocks the sites they
          link to; blocking x.com removes the feed.`
    },
    {
      q: 'Can I block X only at night?',
      a: `Not X alone: SELFCNTRL's curfews cut the internet entirely on a
          schedule, per weekday, to the minute, so a curfew from 11pm to 7am
          takes X with it. Blocking x.com itself is permanent until you
          loosen it, twenty-four hours later.`
    },
    {
      q: 'Does blocking x.com stop the X app?',
      a: `Yes. The app talks to the same domains; a DNS block on x.com and
          twitter.com stops it loading.`
    }
  ]
}

export default guide
